import React, { createContext, useState, useEffect } from 'react';

import { updateIsNewFeaturesShown } from 'services/settings';
import {
  getBusiness,
  getUserFullData,
  updateFeatureConfiguration
} from 'services/auth';
import { getPurchasedSubscription } from 'services/subscriptions';
import { PLAN_STATUS } from 'constants/subscription';
import { isUserAuth } from 'utils/helpers';
import { setPickupFlag } from 'utils/pickups';
import { isUserFullyActivated } from 'utils/features-access';
import { DatesProvider } from 'contexts/dates.context';
import { DEFAULT_PICKUP_CUTOFF_TIME } from 'constants/pickups';
import { BASIC_THEME, STORE_EDITOR_MODES } from 'constants/sllr';
import { PERMISSIONS_MODULES_ID } from 'constants/team-members';
import {
  getOrdersAndPickupsAccess,
  userHasTabAccess
} from 'utils/permissions/permissions-apply';

import { notify } from 'components/Notify/Notify';
import CreateEditPickupModal from 'components/Pickups/components/CreateEditPickupModal/CreateEditPickupModal';
import PrepareOrdersModal from 'components/Orders/components/PrepareOrdersModal/PrepareOrdersModal';

export const ContextWrapper = createContext({ name: '' });

const { DELIVERIES } = PERMISSIONS_MODULES_ID;

export const WrapperProvider = ({ children }) => {
  const [newFeatures, setNewFeatures] = useState({});
  const [businessInfo, setBusinessInfo] = useState({});
  const [preventNavigation, setPreventNavigation] = useState(false);
  const [draftOrdersCount, setDraftOrdersCount] = useState(0);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [userFullData, setUserFullData] = useState({});
  const [isSllrSubscribed, setIsSllrSubscribed] = useState(null);

  const [toursFlags, setToursFlags] = useState({
    showOrdersPageTourStep: false,
    showStartSellingTourStep: false
  });
  const [pickupCutoffTime, setPickupCutoffTime] = useState(
    DEFAULT_PICKUP_CUTOFF_TIME
  );
  const [serverTimeData, setServerTimeData] = useState({
    timeZone: {}
  });

  const checkIfUserHasNewFeatures = (data) => {
    const featuresObj = data;
    for (const key in featuresObj) {
      if (data[key] === true && key !== 'bostaBox') {
        delete featuresObj[key];
      }
    }
    return featuresObj;
  };

  const handleUpdateFeaturesConfigurations = async (key, value = true) => {
    try {
      await updateFeatureConfiguration({ [key]: value });
      setBusinessInfo((prev) => ({
        ...prev,
        featuresConfigurations: {
          ...prev?.featuresConfigurations,
          [key]: value
        }
      }));
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  const fetchUserFullData = async () => {
    try {
      const userData = await getUserFullData();
      setUserFullData(userData);
    } catch (error) {
      notify({ msg: error.message, error });
    }
    setIsFetchingData(false);
  };

  const updateLocalStorageWithUserBusinessData = (businessData) => {
    if (!businessData) {
      return;
    }
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    const { businessLegalInfo, bankInfo, defaultPickupLocation, storeInfo } =
      businessData;
    if (bankInfo) {
      userInfo.user.businessAdminInfo = {
        ...userInfo.user.businessAdminInfo,
        bankInfo
      };
    }
    if (defaultPickupLocation) {
      userInfo.user.businessAdminInfo = {
        ...userInfo.user.businessAdminInfo,
        defaultPickupLocation
      };
    }
    // if (businessLegalInfo?.haveCommercialId !== undefined) {
    //   if (businessLegalInfo?.haveCommercialId) {
    //     userInfo.user.businessAdminInfo = {
    //       ...userInfo.user.businessAdminInfo,
    //       businessLegalInfo: {
    //         ...businessLegalInfo,
    //         haveCommercialId: true,
    //         businessNationalId: false,
    //         businessTaxId: businessLegalInfo.businessTaxId ? true : false,
    //         legalBusinessName: businessLegalInfo.legalBusinessName
    //       }
    //     };
    //   } else {
    //     userInfo.user.businessAdminInfo = {
    //       ...userInfo.user.businessAdminInfo,
    //       businessLegalInfo: {
    //         ...businessLegalInfo,
    //         haveCommercialId: false,
    //         businessNationalId: businessLegalInfo.businessNationalId
    //           ? true
    //           : false,
    //         businessTaxId: false
    //       }
    //     };
    //   }
    // } else if (businessLegalInfo?.businessNationalId) {
    //   userInfo.user.businessAdminInfo = {
    //     ...userInfo.user.businessAdminInfo,
    //     businessLegalInfo: {
    //       ...businessLegalInfo,
    //       businessNationalId: true
    //     }
    //   };
    // }
    if (businessLegalInfo) {
      userInfo.user.businessAdminInfo = {
        ...userInfo.user.businessAdminInfo,
        businessLegalInfo
      };
    }
    if (storeInfo) {
      userInfo.user.businessAdminInfo = {
        ...userInfo.user.businessAdminInfo,
        storeInfo: storeInfo
      };
    } else {
      localStorage.setItem('showNoSllrBanner', true);
    }
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
  };

  const getUserInfo = async () => {
    if (!isUserAuth() && JSON.parse(localStorage.getItem('userInfo'))?.token) {
      fetchUserFullData();
      return;
    } else if (!isUserAuth()) {
      setIsFetchingData(false);
      return;
    }
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const businessData = await getBusiness(
        userInfo.user.businessAdminInfo.businessId
      );
      const featuresObj = checkIfUserHasNewFeatures(
        businessData?.newfeatureView
      );
      setNewFeatures(featuresObj);
      setBusinessInfo({
        ...businessData,
        userLoggedInInfo: JSON.parse(localStorage?.getItem('userInfo'))?.user
      });
      setIsFetchingData(false);
      return businessData;
    } catch (error) {
      notify({ msg: error.message, error });
    }
    setIsFetchingData(false);
  };

  const updateUserLoggedInInfo = (value) => {
    setBusinessInfo({
      ...businessInfo,
      userLoggedInInfo: { ...businessInfo?.userLoggedInInfo, ...value }
    });
  };

  const fetchSllrSubscriptionStatus = async () => {
    try {
      const { purchasedSubscriptions } = await getPurchasedSubscription();
      const hasActiveSllrSubscription = !!purchasedSubscriptions?.some(
        (purchasedSubscription) =>
          purchasedSubscription.status === PLAN_STATUS.ACTIVE &&
          purchasedSubscription.subscription?.subscriptionFeaturesKeys?.includes(
            'SLLR_STORE'
          )
      );
      setIsSllrSubscribed(hasActiveSllrSubscription);
    } catch (error) {
      setIsSllrSubscribed(false);
    }
  };

  useEffect(() => {
    if (isUserAuth()) {
      fetchSllrSubscriptionStatus();
    }
  }, []);

  useEffect(() => {
    getUserInfo().then((businessData) => {
      updateLocalStorageWithUserBusinessData(businessData);
      userHasTabAccess(DELIVERIES, businessData?.userSllrPermissions);
      if (
        isUserAuth() &&
        isUserFullyActivated &&
        userHasTabAccess(DELIVERIES, businessData?.userSllrPermissions)
      ) {
        async function checkPickpFlag() {
          await setPickupFlag();
        }
        checkPickpFlag();
      }
    });
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateBusinessInfo();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const updateShownFeature = async () => {
    let path = window.location.pathname.trim().split('/')[1];
    const pathMapper = {
      shop: 'businessFlyers',
      inventory: 'fulfillmentInfo',
      products:
        window.location.pathname.trim().split('/')[2] === 'reviews'
          ? 'productReviews'
          : window.location.pathname.trim().split('/')[2] === 'categories'
          ? 'categoriesPage'
          : window.location.pathname.trim().split('/')[2] === 'sllr'
          ? 'productsSllrPage'
          : 'productsPage',
      'store-design': 'storeDesign',
      customers: 'isBusinessCustomersTabViewed',
      default: path
    };

    path = pathMapper[path] || pathMapper.default;

    const featureNeedsUpdate = Object.keys(newFeatures)?.filter((featureName) =>
      featureName.includes(path)
    );

    if (featureNeedsUpdate.length) {
      try {
        await updateIsNewFeaturesShown({ [featureNeedsUpdate[0]]: false });
        const { [featureNeedsUpdate[0]]: _, ...restFeatures } = newFeatures;
        setNewFeatures(restFeatures);
      } catch (error) {
        //
      }
    }
  };

  const updateBusinessInfo = async () => {
    try {
      const businessData = await getUserInfo();
      updateLocalStorageWithUserBusinessData(businessData);
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  const handleOpenCreateEditPickupModal = (data) => {
    const { openModal } = data;
    const { HIDE_SCHEDULE_PICKUP, HIDE_EDIT_PICKUP } =
      getOrdersAndPickupsAccess(businessInfo?.userSllrPermissions);

    if (!HIDE_SCHEDULE_PICKUP || !HIDE_EDIT_PICKUP) {
      openModal(CreateEditPickupModal, {
        handleOpenPrepareOrderModal,
        ...data
      });
    }
  };

  const handleOpenPrepareOrderModal = (data) => {
    const { openModal } = data;
    openModal(PrepareOrdersModal, {
      handleOpenCreateEditPickupModal,
      ...data
    });
  };

  const handleUpdateStoreCategories = async (
    newCategory = {},
    categoryIdToDelete = null
  ) => {
    const storeStructure =
      businessInfo?.featuresConfigurations?.storeDesignData?.storeStructure;

    const choosenThemeId = storeStructure.choosenThemeId || BASIC_THEME;
    const sections = storeStructure[choosenThemeId].HomePageSections;

    const newHomePageSections = sections.map((section) => {
      if (section.type === STORE_EDITOR_MODES.CATEGORIES) {
        const updatedCategories = section.selectedCategories
          .filter((category) => category._id !== categoryIdToDelete)
          .map((category) => {
            return category._id === newCategory.id
              ? { ...newCategory }
              : category;
          });
        return { ...section, selectedCategories: updatedCategories };
      } else return section;
    });

    await handleUpdateFeaturesConfigurations('storeDesignData', {
      ...businessInfo?.featuresConfigurations?.storeDesignData,
      storeStructure: {
        ...storeStructure,
        ...(!storeStructure.createdAt && {
          createdAt: new Date().toISOString()
        }),
        choosenThemeId,
        [choosenThemeId]: {
          ...storeStructure[choosenThemeId],
          HomePageSections: newHomePageSections
        }
      }
    });
  };

  //check if wallet enabled for this user and get all business information

  return (
    <ContextWrapper.Provider
      value={{
        newFeatures,
        businessInfo,
        setBusinessInfo,
        updateShownFeature,
        updateBusinessInfo,
        preventNavigation,
        setPreventNavigation,
        draftOrdersCount,
        setDraftOrdersCount,
        isFetchingData,
        toursFlags,
        setToursFlags,
        handleUpdateFeaturesConfigurations,
        userFullData,
        setUserFullData,
        updateUserLoggedInInfo,
        handleOpenCreateEditPickupModal,
        handleOpenPrepareOrderModal,
        pickupCutoffTime,
        handleUpdateStoreCategories,
        serverTimeData,
        isSllrSubscribed
      }}
    >
      <DatesProvider
        setPickupCutoffTime={setPickupCutoffTime}
        setServerTimeData={setServerTimeData}
        serverTimeData={serverTimeData}
        pickupCutoffTime={pickupCutoffTime}
      >
        {children}
      </DatesProvider>
    </ContextWrapper.Provider>
  );
};
